import { Star, ExternalLink } from 'lucide-react';
import PropTypes from 'prop-types';
import { clsx } from 'clsx';

/**
 * Google Review Card - displays a single 5-star Google review
 */
const GoogleReviewCard = ({ review, className = '' }) => {
  const initial = review.author_name?.charAt(0)?.toUpperCase() || 'U';

  // Generate consistent color from name
  const colors = [
    'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500',
    'bg-yellow-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
  ];
  const colorIndex = review.author_name
    ? review.author_name.charCodeAt(0) % colors.length
    : 0;

  return (
    <article
      className={clsx(
        'bg-white rounded-2xl p-6 shadow-soft border border-neutral-100',
        'hover:shadow-md transition-shadow duration-300',
        'flex flex-col h-full',
        className
      )}
    >
      {/* Google branding + stars */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
          ))}
        </div>
        <svg className="w-5 h-5" viewBox="0 0 24 24" aria-label="Google">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
      </div>

      {/* Review text */}
      <blockquote className="flex-1 mb-4">
        <p className="text-neutral-700 leading-relaxed text-sm line-clamp-4">
          &ldquo;{review.text}&rdquo;
        </p>
      </blockquote>

      {/* Author info */}
      <footer className="flex items-center gap-3 pt-4 border-t border-neutral-100">
        {review.profile_photo_url ? (
          <img
            src={review.profile_photo_url}
            alt=""
            className="w-10 h-10 rounded-full object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className={clsx('w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm', colors[colorIndex])}>
            {initial}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <cite className="not-italic font-semibold text-neutral-900 text-sm block truncate">
            {review.author_name}
          </cite>
          <p className="text-xs text-neutral-400">{review.relative_time_description}</p>
        </div>
      </footer>
    </article>
  );
};

GoogleReviewCard.propTypes = {
  review: PropTypes.shape({
    author_name: PropTypes.string.isRequired,
    profile_photo_url: PropTypes.string,
    rating: PropTypes.number.isRequired,
    relative_time_description: PropTypes.string,
    text: PropTypes.string.isRequired,
  }).isRequired,
  className: PropTypes.string,
};

export default GoogleReviewCard;
